import m, {Vnode, VnodeDOM} from "mithril";
import {TextboxItem} from "../../../model/items/textbox";
import {CheckboxItem} from "../../../model/items/checkbox";
import CheckboxCell from "../../shared/components/FormCell/CheckboxCell";
import TMapPinSetting from "./TMapPinSetting";
import ICustomFormSettings from "../../ICustomFormSettings";
import SeznamMapCustomForm from "../SeznamMapCustomForm";
import {loadStyles} from "../../../model/utils";
import L from "leaflet";

export default class MapPin extends SeznamMapCustomForm {

    public readonly self: MapPin;
    private map!: L.Map; // map is set in initMapComponent()

    private loadingPhase = false;
    private preventPinAdd = false;

    private readonly outputPins: TextboxItem;
    private readonly outputCount: TextboxItem;
    private readonly outputValid: CheckboxItem;
    private readonly checkboxCellGU?: CheckboxCell;

    private readonly mapSetting: TMapPinSetting;

    private pins: [number,number][] = [];
    private markers: Set<L.Marker> = new Set<L.Marker>();
    private errorMessage: string = '';

    constructor(settings: ICustomFormSettings) {
        super(settings);
        this.self = this;

        const layout = settings.form.grid.layout;
        this.outputPins = <TextboxItem>layout[0][1].cellItems[0];
        this.outputCount = <TextboxItem>layout[1][1].cellItems[0];
        this.outputValid = <CheckboxItem>layout[2][1].cellItems[0];
        const cellItemGU = <CheckboxItem>layout[3]?.[1]?.cellItems?.[0];
        if (cellItemGU) {
            this.checkboxCellGU = new CheckboxCell(cellItemGU.cell, (cellItem) => {
                cellItem.set();
                if (cellItemGU?.getVariable()) {
                    this.reset();
                }
            });
        }

        try {
            this.mapSetting = JSON.parse(settings.form.aux1 ?? '{}');
        } catch (e) { // JSON parse error
            console.warn('customForm-Map-pin: Map settings is not valid json.')
            this.mapSetting = {};
        }

        if (!settings.form.hint) {
            this.settings.app.header.hint = settings.form.questionary.messages.get(this.mapSetting.minPoints ? 'mapPinHintMinLimit' : 'mapPinHint')
                .replace('{min}', ''+this.mapSetting.minPoints);
        }
    }

    public checkRequirements(): boolean {
        if (!this.outputPins) {
            console.warn("customForm-Map-pin: Missing output for pins, cell on row 0, column 1.")
            return false;
        }
        if (!this.outputCount) {
            console.warn("customForm-Map-pin: Missing output for count, cell on row 1, column 1.")
            return false;
        }
        if (!this.outputValid) {
            console.warn("customForm-Map-pin: Missing checkbox for validation, cell on row 2, column 1.")
            return false;
        }
        if (this.checkboxCellGU && !this.checkboxCellGU.checkbox.globalUnknown) {
            console.warn(`customForm-Map-pin: Checkbox 'GlobalGU' must be globalUnknown, cell on row 3, column 1.`)
            return false;
        }

        return true;
    }

    private async initMapComponent(mapElement: HTMLElement) {
        this.self.loadingPhase = true;
        m.redraw();

        await loadStyles('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css', {integrity: "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=", crossorigin: ""});

        const map = L.map(mapElement);
        this.self.map = map;

        this.initMapCenter(map);
        this.initMapPins(map);
        this.initMapGeometry(map);
        this.attachMapEvents(map);
        this.self.loadingPhase = false;

        m.redraw();
    }

    private initMapCenter(map: L.Map): void {
        let [lat, lng] = [50,14];
        if (this.mapSetting?.center?.length) {
            // Map center is defined in mapSetting
            [lat, lng] = this.mapSetting.center;
        }
        map.setView([lat, lng])

        // Map center on depends on device location
        if (this.mapSetting?.useDeviceLocation) {
            window.navigator.geolocation?.getCurrentPosition((pos) => {
                map.setView([pos.coords.latitude, pos.coords.longitude]);
            });
        }

        // Map zoom
        if (this.mapSetting?.zoom) {
            map.setZoom(this.mapSetting.zoom);
        }

        L.tileLayer(SeznamMapCustomForm.getTilesUrl(), {
            minZoom: 0,
            maxZoom: 19,
            attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>'
        }).addTo(map);
    }

    private initMapPins(map: L.Map): void {
        this.self.pins = this.loadPins();
        this.self.pins.forEach(pin => this.addMarkerToMap(pin, map));
    }

    private initMapGeometry(map: L.Map): void {
        if (this.mapSetting.geometry?.length) {
            this.mapSetting.geometry.forEach(geometry => {
                switch (geometry.type) {
                    case 'polygon': {
                        L.polygon(geometry.points, geometry.options).addTo(map);
                        break;
                    }
                    case 'polyline': {
                        L.polyline(geometry.points, geometry.options).addTo(map);
                        break;
                    }
                    default: {
                        console.error(`Unsupported geometry type '${geometry.type}'`);
                    }
                }
            })
        }
    }

    private attachMapEvents(map: L.Map): void {

        map.on('click', e => {
            if (this.preventPinAdd) {
                return;
            }
            if (this.checkboxCellGU?.checkbox.getVariable()) {
                this.checkboxCellGU?.checkbox.setVariable(false);
            }
            const coordinates: [number, number] = [e.latlng.lat, e.latlng.lng];
            if (this.canAddPin(coordinates)) {
                this.self.pins.push(coordinates);
                this.self.savePins();
                this.self.addMarkerToMap(coordinates, map);
            }
            m.redraw();
        });
    }

    private canAddPin(coordinates: [number, number]): boolean {
        if (this.mapSetting.maxPoints && this.self.pins.length >= this.mapSetting.maxPoints) {
            this.setErrorMessage('mapPinMaxPointsLimit');
            m.redraw();
            return false;
        }
        if (this.mapSetting.area?.length === 2) {
            const [lt,br] = this.mapSetting.area;
            const lat = coordinates[0];
            const lng = coordinates[1];
            if (lat > lt[0] || lat < br[0] || lng < lt[1] || lng > br[1]) {
                this.setErrorMessage('mapPinOutOfArea');
                m.redraw();
                return false;
            }
        }
        return true;
    }

    private addMarkerToMap(coordinates: [number, number], map: L.Map): void {
        const marker = L.marker(coordinates).addTo(map);
        marker.on('click', () => {
            // remove from map
            marker.removeFrom(map);

            // remove from saved pins
            this.pins = this.pins.filter(pin => String(pin) !== String(coordinates));

            // remove from markers set
            this.markers.delete(marker);

            // save to the questionary
            this.savePins();

            m.redraw();
        })
        this.markers.add(marker);
    }


    private loadPins(): [number,number][] {
        const raw = this.outputPins.getVariable() || '[]';
        try {
            return <[number,number][]>JSON.parse(raw);
        } catch (e) {
            return [];
        }
    }

    private savePins(): void {
        this.outputPins.setVariable(this.self.pins?.length ? JSON.stringify(this.self.pins) : '');
        this.outputCount.setVariable(this.self.pins.length);
        this.outputValid.setVariable(this.self.isValid());
    }

    private isValid(): boolean {
        return Boolean(
            this.mapSetting.minPoints && this.self.pins.length >= this.mapSetting.minPoints
            && this.mapSetting.maxPoints && this.self.pins.length <= this.mapSetting.maxPoints
        );
    }

    private reset(): void {
        this.self.outputPins.reset();
        this.self.outputCount.reset();
        this.self.outputValid.reset();

        this.self.pins = [];
        this.markers.forEach(marker => {
           marker.removeFrom(this.map);
        });
        this.markers = new Set<L.Marker>();
    }

    private setPreventAddPin(): void {
        this.preventPinAdd = true;
        setTimeout(() => this.preventPinAdd = false, 133);
    }

    private setErrorMessage(message: string): void {
        this.errorMessage = message;
        setTimeout(() => {
            this.errorMessage = '';
            m.redraw();
        }, 4000);
    }


    public view(): Vnode {
        const messages = this.settings.form.questionary.messages;
        return this.viewLayout([
            this.loadingPhase ? this.viewLoading() : null,
            m('.map-container', {
                ontouchmove: (e: TouchEvent) => {
                    e.preventDefault();
                },
            }, [
                m('.map', {
                    oncreate: async (vnode: VnodeDOM) => {
                        await this.initMapComponent(<HTMLElement>vnode.dom);
                    },
                }, this.map ? '' : messages.get('loading')),
                m('.error-alert', {
                    style: {opacity: this.errorMessage ? 1 : 0}
                }, [
                    m('.', {}, messages.get(this.errorMessage || '.').replace('{max}', String(this.mapSetting.maxPoints))),
                ])
            ]),
            this.checkboxCellGU ? m(this.checkboxCellGU) : null,
        ]);
    }

    public onremove(): void {
        if (!this.settings.form.hint) {
            this.settings.app.header.hint = '';
        }
    }

}